import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { QuickAddFAB } from '../components/QuickAddFAB';
import { EntrepriseManagerShared } from '../components/EntrepriseManagerShared';
import { ChantierFormSheet } from '../components/chantiers/ChantierFormSheet';
import { DossierSheet } from '../components/dossier/DossierSheet';
import { LotsManagerSheet } from '../components/lots/LotsManagerSheet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, Buildings, MapPin, User, CalendarBlank, MagnifyingGlass,
  PencilSimple, Trash, ArrowLeft, UsersThree, CaretDown, CaretUp,
  Notebook, FolderOpen, Stack, Storefront, HardHat,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ChantiersPage() {
  const [chantiers, setChantiers] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingChantier, setEditingChantier] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedChantier, setExpandedChantier] = useState(null);
  const [formData, setFormData] = useState({
    name: '', address: '', client_name: '', start_date: '',
    end_date: '', status: 'active', description: '', type: 'opc'
  });
  const [submitting, setSubmitting] = useState(false);
  const [dossierChantier, setDossierChantier] = useState(null);
  const [lotsChantier, setLotsChantier] = useState(null);

  const fetchChantiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantiers?type=opc`, { withCredentials: true });
      setChantiers((res.data || []).filter(c => c && c.id));
    } catch (error) {
      setChantiers([]);
    } finally { setLoading(false); }
  }, [setChantiers, setLoading]);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const openCreateSheet = () => {
    setEditingChantier(null);
    setFormData({ name: '', address: '', client_name: '', start_date: '', end_date: '', status: 'active', description: '', type: 'opc' });
    setIsSheetOpen(true);
  };

  const openEditSheet = (c) => {
    setEditingChantier(c);
    setFormData({
      name: c.name || '', address: c.address || '', client_name: c.client_name || '',
      start_date: c.start_date || '', end_date: c.end_date || '', status: c.status || 'active',
      description: c.description || '', type: 'opc'
    });
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingChantier) {
        await axios.put(`${API}/chantiers/${editingChantier.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/chantiers`, formData, { withCredentials: true });
      }
      setIsSheetOpen(false);
      setEditingChantier(null);
      fetchChantiers();
    } catch (_err) { toast.error('Erreur sauvegarde chantier'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (chantierId) => {
    try {
      await axios.delete(`${API}/chantiers/${chantierId}`, { withCredentials: true });
      setDeleteConfirm(null);
      fetchChantiers();
    } catch (_err) { toast.error('Erreur suppression chantier'); }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: <span className="chantix-badge chantix-badge-success">Actif</span>,
      completed: <span className="chantix-badge chantix-badge-info">Terminé</span>,
      paused: <span className="chantix-badge chantix-badge-warning">En pause</span>,
    };
    return map[status] || <span className="chantix-badge chantix-badge-neutral">{status || 'N/A'}</span>;
  };

  const filteredChantiers = (chantiers || []).filter(c => {
    if (!c) return false;
    const s = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) || (c.client_name || '').toLowerCase().includes(s) || (c.address || '').toLowerCase().includes(s);
  });

  if (loading) {
    return <AppShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-[#0055FF]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="chantiers-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]">Chantiers</h1>
              <p className="text-[#4B5563] mt-1">{chantiers.length} chantier(s)</p>
            </div>
          </div>
          <Button onClick={openCreateSheet} className="bg-[#0055FF] hover:bg-blue-700" data-testid="new-chantier-btn">
            <Plus size={20} className="mr-2" /> Nouveau chantier
          </Button>
        </div>

        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input placeholder="Rechercher un chantier..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-14" data-testid="chantier-search-input" />
        </div>

        <div className="grid gap-4">
          {filteredChantiers.length > 0 ? filteredChantiers.map((chantier) => (
            <Card key={chantier.id} className="chantix-card" data-testid={`chantier-card-${chantier.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0"
                    onClick={() => setExpandedChantier(expandedChantier === chantier.id ? null : chantier.id)}
                    style={{ cursor: 'pointer' }}>
                    <div className="p-3 rounded-lg bg-blue-100 flex-shrink-0">
                      <Buildings size={24} className="text-[#0055FF]" weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-[#111827]">{chantier.name}</h3>
                        {getStatusBadge(chantier.status)}
                        {expandedChantier === chantier.id ? <CaretUp size={16} /> : <CaretDown size={16} />}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-[#4B5563] flex items-center gap-2">
                          <MapPin size={16} className="flex-shrink-0" /><span className="truncate">{chantier.address || 'Non renseigne'}</span>
                        </p>
                        <p className="text-sm text-[#4B5563] flex items-center gap-2">
                          <User size={16} className="flex-shrink-0" /><span className="truncate">{chantier.client_name || 'Non renseigne'}</span>
                        </p>
                        {chantier.start_date && (
                          <p className="text-sm text-[#4B5563] flex items-center gap-2">
                            <CalendarBlank size={16} className="flex-shrink-0" /><span>{chantier.start_date}{chantier.end_date ? ` → ${chantier.end_date}` : ''}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); setLotsChantier(chantier); }}
                      className="h-12 w-12 text-[#0055FF] hover:bg-blue-50"
                      data-testid={`lots-chantier-${chantier.id}`} title={`Lots (${(chantier.lot_ids || []).length})`}>
                      <Stack size={20} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); setDossierChantier(chantier); }}
                      className="h-12 w-12 text-[#0055FF] hover:bg-blue-50"
                      data-testid={`dossier-chantier-${chantier.id}`} title="Dossier documentaire">
                      <FolderOpen size={20} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/chantiers/${chantier.id}/journal`); }}
                      className="h-12 w-12 text-[#FF6B00] hover:bg-orange-50" data-testid={`journal-chantier-${chantier.id}`}
                      title="Journal de chantier"><Notebook size={20} /></Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/chantiers/${chantier.id}/entreprises`); }}
                      className="h-12 w-12 text-emerald-600 hover:bg-emerald-50" data-testid={`vue-entreprise-${chantier.id}`}
                      title="Vue Entreprise (photo/texte/vocal)"><Storefront size={20} /></Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/chantiers/${chantier.id}/site-visits`); }}
                      className="h-12 w-12 text-yellow-600 hover:bg-yellow-50" data-testid={`site-visits-${chantier.id}`}
                      title="Visites de chantier"><HardHat size={20} /></Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); openEditSheet(chantier); }}
                      className="h-12 w-12" data-testid={`edit-chantier-${chantier.id}`}><PencilSimple size={20} /></Button>
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(chantier); }}
                      className="h-12 w-12 text-red-600 hover:bg-red-50" data-testid={`delete-chantier-${chantier.id}`}><Trash size={20} /></Button>
                  </div>
                </div>
                {expandedChantier === chantier.id && (
                  <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                    <div className="flex items-center gap-2 mb-2">
                      <UsersThree size={16} className="text-[#0055FF]" />
                      <span className="text-sm font-medium text-[#111827]">Entreprises intervenantes</span>
                    </div>
                    <EntrepriseManagerShared chantierId={chantier.id} variant="opc" />
                  </div>
                )}
              </CardContent>
            </Card>
          )) : (
            <Card className="chantix-card">
              <CardContent className="py-12 text-center">
                <Buildings size={48} className="mx-auto mb-4 text-[#4B5563] opacity-50" />
                <h3 className="font-semibold text-[#111827] mb-2">{searchTerm ? 'Aucun resultat' : 'Aucun chantier'}</h3>
                <p className="text-[#4B5563] mb-4">{searchTerm ? 'Modifiéz votre recherche' : 'Commencez par créer votre premier chantier'}</p>
                {!searchTerm && (
                  <Button className="bg-[#0055FF] hover:bg-blue-700" onClick={openCreateSheet}>
                    <Plus size={20} className="mr-2" /> Créer un chantier
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ChantierFormSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}
        editingChantier={editingChantier} formData={formData} setFormData={setFormData}
        submitting={submitting} onSubmit={handleSubmit} />

      <DossierSheet
        open={!!dossierChantier}
        onOpenChange={(v) => { if (!v) setDossierChantier(null); }}
        chantier={dossierChantier}
      />

      <LotsManagerSheet
        open={!!lotsChantier}
        onOpenChange={(v) => { if (!v) setLotsChantier(null); }}
        chantier={lotsChantier}
        onChange={fetchChantiers}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimér le chantier ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimér le chantier "{deleteConfirm?.name}" ? Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)} className="h-12 bg-red-600 hover:bg-red-700">Supprimér</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickAddFAB chantiers={chantiers} />
    </AppShell>
  );
}
